import React, { useState } from 'react'
import { addToastMsg } from '../services/amm'
import { Slider } from '@mui/material'
import BoxHeaderNew, { DEFAULT_SLIPPAGE } from './BoxHeader/BoxHeader'
import { useStoreActions, useStoreState } from '../model/hooks'
// import { isTransactionInProgress } from '../model/transaction/helpers'
import { useWalletContext } from './WalletContext'
import { removeLiquidity } from '../services/position'
import { Network } from '../services/token.service'
import { GAtrack } from '../services/ga'
import { numFormat } from '../blockchainUtils/math'
import PrimaryButton from './PrimaryButton'
import { useTransactionFollow } from '../hooks/useTransactionFollow'

export default function RemoveLiquidity(props: {
    onBackClick: () => void
    onBackToPositions: () => void
    positionReduxIndex: number
    poolAddress: string
    token1Amount: string
    token2Amount: string
    balance: string
    token1Address: string
    token2Address: string
}) {
    const { token1Address, token2Address, positionReduxIndex, onBackClick, onBackToPositions } = props
    const { walletAddress: connectedAddress, networkName: network } = useWalletContext()

    const [percent, setPercent] = useState(25)
    const [removeLiquidityInProgress, setRemoveLiquidityInProgress] = useState(false)
    const [slippage, setSlippage] = useState(Number(DEFAULT_SLIPPAGE))
    const loadTransaction = useStoreActions((actions) => actions.transaction.loadTransaction)
    const [txHash, setTxHash] = useState('')
    const token1 = useStoreState((state) => state.tokens.getToken(network!, token1Address))
    const token2 = useStoreState((state) => state.tokens.getToken(network!, token2Address))
    const loadBalances = useStoreActions((actions) => actions.tokens.loadBalances)
    const clPosition = useStoreState((state) => state.clPools.getCLPositionByIndex(network!, positionReduxIndex))
    const loadPositons = useStoreActions((actions) => actions.clPools.loadPositons)
    useTransactionFollow(
        txHash,
        () => {
            loadPositons({ network: network as Network, address: connectedAddress!, selectedPosition: positionReduxIndex })
            loadBalances({ network: network as Network, walletAddress: connectedAddress! })
            setTxHash('')
            onBackClick()
            setRemoveLiquidityInProgress(false)

            if (percent === 100) {
                onBackToPositions()
                return
            }
        },
        () => {
            console.log('FAILED!!!')
            setTxHash('')
            setRemoveLiquidityInProgress(false)
        }
    )

    const updateState = (newPercent: number) => {
        setPercent(newPercent)
    }

    const handleSliderChange = (event: any, newPercent: number) => {
        updateState(newPercent)
    }

    const handleWithdraw = async () => {
        setRemoveLiquidityInProgress(true)
        if (!clPosition) return
        console.log('Withdraw percent: ', percent)

        try {
            console.log('YSYS calling removeLiq, pos=', clPosition)
            const response = await removeLiquidity(
                network as Network,
                {
                    fee: clPosition!.fee!,
                    feeGrowthInside0LastX128: clPosition!.feeGrowthInside0LastX128!,
                    feeGrowthInside1LastX128: clPosition!.feeGrowthInside1LastX128!,
                    liquidity: clPosition?.liquidity,
                    positionIndex: clPosition?.positionIndex,
                    tickLower: clPosition?.tickLower,
                    tickUpper: clPosition?.tickUpper,
                    token0: clPosition?.token0,
                    token1: clPosition?.token1,
                    tokensOwed0: clPosition?.tokensOwed0,
                    tokensOwed1: clPosition?.tokensOwed1
                },
                clPosition.value,
                clPosition.fees ?? {
                    token1Address: '',
                    token2Address: '',
                    token1AmountWei: '0',
                    token1Amount: '0',
                    token2AmountWei: '0',
                    token2Amount: '0'
                },
                percent / 100,
                slippage / 100
            )
            if (network === 'mainnet-alpha') GAtrack('tx_initiated_remove_liquidity', 'tx_initiated_remove_liquidity', 'remove_liquidity')
            // @ts-ignore
            if (response && response.tx) {
                const timestampSent = new Date().getTime()

                // @ts-ignore
                loadTransaction({ txPayload: { [response.tx.hash]: { ...response.tx, labelFields: { timestampSent: timestampSent } } }, isGoogleLog: network === 'mainnet-alpha' })

                // @ts-ignore
                setTxHash(response.tx.hash)
                // @ts-ignore
                // GAtrack('NewTx', 'remove_liquidity', response.tx.hash)
                const label = `remove liquidity token1Address = ${token1} token2Address = ${token2}`
                if (network === 'mainnet-alpha') GAtrack('tx_sent_remove_liquidity', 'tx_sent_remove_liquidity', label)
            } else {
                setRemoveLiquidityInProgress(false)
            }
            // setRemoveLiquidityInProgress(false)
        } catch (e: any) {
            setRemoveLiquidityInProgress(false)

            if (e.message.includes('User abort')) {
                addToastMsg('Remove Liquidity: User Aborted', 'error')
            }
            setRemoveLiquidityInProgress(false)
        }
    }

    function renderPercentButton(number: number) {
        const isSelected = number === percent
        const text = number === 100 ? 'Max' : number + '%'
        return (
            <div
                onClick={() => handleSliderChange(undefined, number)}
                className={`cursor-pointer text-4 border ${isSelected ? 'border-[#0AE8F9]' : 'border-[#0C3F6D]'} rounded-xl py-1 px-2 mr-2`}>
                {text}
            </div>
        )
    }

    function renderTokensHeader() {
        if (!token1 || !token2) return null

        return (
            <div className='w-full flex items-center lg:mb-6 mb-3'>
                <div className='flex items-center'>
                    <img src={token2.icon} className=' w-9 h-9 mr-[-6px]' />
                    <img src={token1.icon} className=' w-9 h-9 ml-[-6px]' />
                </div>
                <span className='font-bold ml-1 text-[18px]'>
                    {token2?.symbol}/{token1?.symbol}
                </span>
            </div>
        )
    }

    function renderTokenLine(tokenIndex: number, isFee = false, className = '') {
        const tokenObject = tokenIndex === 1 ? token1 : token2
        if (!tokenObject || !clPosition || !clPosition.value) return null

        const fee = tokenIndex === 1 ? clPosition?.fees?.token1Amount : clPosition?.fees?.token2Amount
        const pooled = tokenIndex === 1 ? clPosition?.value.token1Amount : clPosition?.value?.token2Amount
        return (
            <div className={`flex items-center text-white text-[16px] justify-between ${className}`}>
                <div className='flex'>
                    <img src={tokenObject.icon} height={24} width={24} className='shrink-0 max-w-[24px] max-h-[24px] mr-1' />
                    <span>{isFee ? `${tokenObject.symbol} Fee Earned` : `Pooled ${tokenObject.symbol}`}</span>
                </div>
                <span className='font-bold'>{isFee ? numFormat(fee || '0') : numFormat(pooled || '')}</span>
            </div>
        )
    }

    function renderDetailsContent() {
        if (!token1 || !token2) return null
        return (
            <div className='bg-[#0C1647] rounded-2xl lg:p-6 p-4'>
                <div>
                    {renderTokenLine(1, false)}
                    {renderTokenLine(2, false, 'mt-3')}
                    <div className='w-full h-[1px] bg-[#0C3F6D] mt-3' />
                    {renderTokenLine(1, true, 'mt-3')}
                    {renderTokenLine(2, true, 'mt-3')}
                </div>
            </div>
        )
    }

    return (
        <div className='max-w-[528px] m-auto'>
            <div className='w-full lg:mb-6 mb-3'>
                <BoxHeaderNew slippage={slippage.toString()} setOutsideSlippage={(val) => setSlippage(val)} className='' name='Remove Liquidity' />
            </div>
            {renderTokensHeader()}
            <div className='bg-[#0C1647] w-full lg:p-6 p-4 rounded-2xl mb-6'>
                <span className='font-bold text-white text-[18px]'>Amount</span>
                <div>
                    <div>
                        <div className='flex flex-col justify-center'>
                            <div className='flex items-center justify-between'>
                                <span className='block text-6 font-bold'>{percent}%</span>
                                <div className='flex items-center'>
                                    {renderPercentButton(25)}
                                    {renderPercentButton(50)}
                                    {renderPercentButton(75)}
                                    {renderPercentButton(100)}
                                </div>
                            </div>
                            <Slider
                                defaultValue={25}
                                valueLabelDisplay='auto'
                                className='pl-[20px] mt-4'
                                sx={{ color: '#0AE8F9', p: 0 }}
                                value={typeof percent === 'number' ? percent : 0}
                                // @ts-ignore
                                onChange={handleSliderChange}
                                aria-labelledby='input-slider'
                            />
                        </div>
                    </div>
                </div>
            </div>
            {renderDetailsContent()}
            <div className='flex justify-center'>
                <PrimaryButton
                    className='button-enabled mt-4 w-full'
                    // fullWidth={true}
                    isDisabled={removeLiquidityInProgress ? true : false}
                    // sx={{ marginTop: '16px' }}
                    isLoading={removeLiquidityInProgress === true}
                    // loadingPosition='start'
                    // @ts-ignore
                    onClick={handleWithdraw}>
                    <span className='normal-case'>{removeLiquidityInProgress ? 'Removing' : 'Remove'}</span>
                </PrimaryButton>
            </div>
        </div>
    )
}
